import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import {
  useAssignTicket,
  useChannels,
  useContacts,
  useCreateSavedView,
  useOperators,
  useSavedViews,
  useTicketList,
  useUpdateTicket,
  type OperatorRow,
  type TicketRow,
} from '../api/queries.js';
import { useAuthStore } from '../auth/auth-store.js';
import {
  Button,
  ChannelIcon,
  ConfirmModal,
  DrawerPanel,
  Field,
  type Column,
  DataTable,
  FilterChip,
  IconButton,
  Input,
  OperatorAvatar,
  PageHeader,
  PriorityBar,
  PriorityChip,
  SLATimer,
  SavedViewTab,
  Select,
  StatusChip,
  Tag,
  timeAgo,
  type ChannelType,
  type TicketPriority,
  type TicketStatus,
} from '../components/ui/index.js';

export function TicketsPage(): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const [activeView, setActiveView] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
  const [channelFilter, setChannelFilter] = useState<Set<string>>(new Set());
  const [priorityFilter, setPriorityFilter] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // C2 saved views modal
  const [newViewOpen, setNewViewOpen] = useState(false);
  // C4 bulk assign modal
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  // C5 close confirm
  const [closeConfirmId, setCloseConfirmId] = useState<string | null>(null);

  const filterParams = useMemo<Record<string, string>>(() => {
    const p: Record<string, string> = {};
    if (activeView === 'mine' && user?.id) p.assigned_to = user.id;
    if (activeView === 'unassigned') p.assigned_to = 'unassigned';
    if (activeView === 'sla') p.sort = 'sla_deadline:asc';
    if (activeView === 'bot') p.status = 'bot_handling';
    if (statusFilter.size) p.status = [...statusFilter].join(',');
    if (channelFilter.size) p.channel_id = [...channelFilter].join(',');
    if (priorityFilter.size) p.priority = [...priorityFilter].join(',');
    if (search) p.q = search;
    if (sortKey) p.sort = `${sortKey}:${sortDir}`;
    return p;
  }, [activeView, user?.id, statusFilter, channelFilter, priorityFilter, search, sortKey, sortDir]);

  const ticketsQ = useTicketList(slug, filterParams);
  const channelsQ = useChannels(slug);
  const operatorsQ = useOperators(slug);
  const contactsQ = useContacts(slug, '');
  const savedViewsQ = useSavedViews(slug);
  const updateMut = useUpdateTicket(slug);
  const assignMut = useAssignTicket(slug);
  const createViewMut = useCreateSavedView(slug);

  const channelsById = useMemo(
    () => new Map((channelsQ.data ?? []).map((c) => [c.id, c])),
    [channelsQ.data],
  );
  const operatorsById = useMemo(
    () => new Map((operatorsQ.data ?? []).map((o) => [o.id, o])),
    [operatorsQ.data],
  );
  const contactsById = useMemo(
    () => new Map((contactsQ.data ?? []).map((c) => [c.id, c])),
    [contactsQ.data],
  );

  const rows = ticketsQ.data ?? [];

  function toggleSort(key: string): void {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  function sortIcon(key: string): string {
    if (sortKey !== key) return 'ti-arrows-sort';
    return sortDir === 'asc' ? 'ti-sort-ascending' : 'ti-sort-descending';
  }

  const columns: Column<TicketRow>[] = [
    {
      key: 'prio',
      label: '',
      width: 14,
      render: (r) => <PriorityBar priority={(r.priority as TicketPriority) ?? 'low'} />,
    },
    {
      key: 'num',
      label: '№',
      width: 70,
      sortable: true,
      render: (r) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontFamily: 'var(--inbx-font-mono)', color: 'var(--inbx-text-tertiary)' }}>
            #{r.number}
          </span>
        </span>
      ),
    },
    {
      key: 'contact',
      label: 'Клиент',
      render: (r) => {
        const c = contactsById.get(r.contactId);
        return c ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <OperatorAvatar user={{ id: c.id, name: c.name ?? 'Клиент' }} size="xs" />
            <span style={{ fontWeight: 500 }}>{c.name ?? c.email ?? '—'}</span>
          </span>
        ) : (
          <span style={{ color: 'var(--inbx-text-tertiary)' }}>{r.contactId.slice(0, 8)}…</span>
        );
      },
    },
    {
      key: 'subject',
      label: 'Тема',
      sortable: true,
      render: (r) => (
        <span style={{ color: 'var(--inbx-text-primary)' }}>
          {r.subject ?? <em style={{ color: 'var(--inbx-text-tertiary)' }}>без темы</em>}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Статус',
      width: 100,
      sortable: true,
      render: (r) => <StatusChip status={r.status as TicketStatus} />,
    },
    {
      key: 'channel',
      label: 'Канал',
      width: 70,
      render: (r) => {
        const ch = channelsById.get(r.channelId);
        return <ChannelIcon channel={(ch?.type ?? 'custom') as ChannelType} size={20} />;
      },
    },
    {
      key: 'operator',
      label: 'Оператор',
      width: 160,
      render: (r) => {
        if (!r.assignedTo) return <span style={{ color: 'var(--inbx-text-tertiary)' }}>—</span>;
        const op = operatorsById.get(r.assignedTo);
        return op ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <OperatorAvatar user={op} size="xs" />
            <span>{op.name}</span>
          </span>
        ) : (
          '—'
        );
      },
    },
    {
      key: 'sla',
      label: 'SLA',
      width: 110,
      sortable: true,
      render: (r) => <SLATimer deadline={r.slaDeadline} breached={r.slaBreached} />,
    },
    {
      key: 'updated',
      label: 'Обновлён',
      width: 110,
      sortable: true,
      render: (r) => (
        <span style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
          {timeAgo(r.lastMessageAt ?? r.createdAt)}
        </span>
      ),
    },
    {
      key: 'tags',
      label: 'Теги',
      render: (r) => (
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {(r.tags ?? []).slice(0, 3).map((t) => (
            <Tag key={t}>{t}</Tag>
          ))}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 100,
      render: (r) => (
        <span className="inbx-table__row-actions" onClick={(e) => e.stopPropagation()}>
          <IconButton
            icon="ti-external-link"
            tooltip="Открыть"
            onClick={() => void navigate({ to: '/inbox', search: { ticket: r.id } })}
          />
          <IconButton
            icon="ti-user-plus"
            tooltip="Назначить себе"
            onClick={() => {
              if (user?.id) assignMut.mutate({ id: r.id, userId: user.id });
            }}
          />
          <IconButton
            icon="ti-circle-check"
            tooltip="Закрыть"
            onClick={() => setCloseConfirmId(r.id)}
          />
        </span>
      ),
    },
  ];

  function clearFilters(): void {
    setStatusFilter(new Set());
    setChannelFilter(new Set());
    setPriorityFilter(new Set());
    setSearch('');
    setSortKey('');
  }

  return (
    <div className="inbx-page">
      <PageHeader
        title="Все тикеты"
        badge={{ count: rows.length }}
        actions={
          <>
            <Input
              placeholder="Поиск по имени, тексту, номеру..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 300 }}
            />
            <IconButton icon="ti-columns" tooltip="Колонки" />
            <IconButton icon="ti-download" tooltip="Экспорт" />
          </>
        }
      />

      {/* Saved views — C2: + кнопка */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          padding: '0 24px',
          borderBottom: '1px solid var(--inbx-border)',
          overflowX: 'auto',
          alignItems: 'center',
        }}
      >
        <SavedViewTab
          label="Все открытые"
          icon="ti-inbox"
          active={activeView === 'all'}
          onClick={() => setActiveView('all')}
        />
        <SavedViewTab
          label="Мои тикеты"
          icon="ti-user"
          active={activeView === 'mine'}
          onClick={() => setActiveView('mine')}
        />
        <SavedViewTab
          label="Горят SLA"
          icon="ti-urgent"
          active={activeView === 'sla'}
          onClick={() => setActiveView('sla')}
        />
        <SavedViewTab
          label="Без оператора"
          icon="ti-user-off"
          active={activeView === 'unassigned'}
          onClick={() => setActiveView('unassigned')}
        />
        <SavedViewTab
          label="У бота"
          icon="ti-robot"
          active={activeView === 'bot'}
          onClick={() => setActiveView('bot')}
        />
        {savedViewsQ.data
          ?.filter((v) => v.scope !== 'system')
          .map((v) => (
            <SavedViewTab
              key={v.id}
              label={v.name}
              icon={v.icon ?? 'ti-star'}
              active={activeView === v.id}
              onClick={() => setActiveView(v.id)}
            />
          ))}
        <button
          type="button"
          onClick={() => setNewViewOpen(true)}
          style={{
            padding: '6px 10px',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: 'var(--inbx-accent)',
            fontSize: 'var(--inbx-fz-sm)',
            whiteSpace: 'nowrap',
          }}
        >
          <i className="ti-plus" /> Новый вид
        </button>
      </div>

      {/* Filter chips */}
      <div
        style={{
          display: 'flex',
          gap: 6,
          padding: '10px 24px',
          borderBottom: '1px solid var(--inbx-border)',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <span
          style={{
            color: 'var(--inbx-text-tertiary)',
            fontSize: 'var(--inbx-fz-sm)',
            padding: '4px 0',
          }}
        >
          Фильтры:
        </span>
        {(['new', 'open', 'pending', 'escalated', 'resolved'] as const).map((s) => (
          <FilterChip
            key={s}
            active={statusFilter.has(s)}
            onClick={() => {
              const next = new Set(statusFilter);
              if (next.has(s)) next.delete(s);
              else next.add(s);
              setStatusFilter(next);
            }}
          >
            <StatusChip status={s} size="sm" />
          </FilterChip>
        ))}
        {(['urgent', 'high', 'medium', 'low'] as const).map((p) => (
          <FilterChip
            key={p}
            active={priorityFilter.has(p)}
            onClick={() => {
              const next = new Set(priorityFilter);
              if (next.has(p)) next.delete(p);
              else next.add(p);
              setPriorityFilter(next);
            }}
          >
            <PriorityChip priority={p} />
          </FilterChip>
        ))}
        {(channelsQ.data ?? []).slice(0, 6).map((c) => (
          <FilterChip
            key={c.id}
            active={channelFilter.has(c.id)}
            onClick={() => {
              const next = new Set(channelFilter);
              if (next.has(c.id)) next.delete(c.id);
              else next.add(c.id);
              setChannelFilter(next);
            }}
            icon={<ChannelIcon channel={c.type as ChannelType} size={14} />}
          >
            {c.name.length > 18 ? c.name.slice(0, 16) + '…' : c.name}
          </FilterChip>
        ))}
        {/* C3: Sort controls */}
        {sortKey && (
          <FilterChip
            active
            onClick={() => {
              setSortKey('');
            }}
          >
            Сортировка: {sortKey} {sortDir === 'asc' ? '↑' : '↓'} ×
          </FilterChip>
        )}
        {(statusFilter.size + channelFilter.size + priorityFilter.size > 0 ||
          search ||
          sortKey) && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Сбросить
          </Button>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          style={{
            padding: '8px 24px',
            background: 'var(--inbx-accent-soft)',
            borderBottom: '1px solid var(--inbx-border)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 'var(--inbx-fz-sm)',
          }}
        >
          <span style={{ fontWeight: 500 }}>{selectedIds.size} выбрано</span>
          <Button
            size="sm"
            variant="secondary"
            icon="ti-user-plus"
            onClick={() => {
              if (user?.id)
                [...selectedIds].forEach((id) => assignMut.mutate({ id, userId: user.id }));
              setSelectedIds(new Set());
            }}
          >
            Назначить себе
          </Button>
          {/* C4: BulkAssignModal */}
          <Button
            size="sm"
            variant="secondary"
            icon="ti-users"
            onClick={() => setBulkAssignOpen(true)}
          >
            Назначить другому
          </Button>
          <Button
            size="sm"
            variant="secondary"
            icon="ti-circle-check"
            onClick={() => {
              [...selectedIds].forEach((id) =>
                updateMut.mutate({ id, patch: { status: 'resolved' } }),
              );
              setSelectedIds(new Set());
            }}
          >
            Закрыть как решённые
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon="ti-x"
            onClick={() => setSelectedIds(new Set())}
            style={{ marginLeft: 'auto' }}
          >
            Снять выбор
          </Button>
        </div>
      )}

      {/* C3: Sortable columns header */}
      {sortKey === '' && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            padding: '4px 24px',
            fontSize: 'var(--inbx-fz-sm)',
            color: 'var(--inbx-text-tertiary)',
          }}
        >
          Сортировка по:{' '}
          {(['updated', 'sla', 'status', 'num'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => toggleSort(k)}
              style={{
                color: 'var(--inbx-accent)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 'inherit',
              }}
            >
              <i className={sortIcon(k)} />{' '}
              {k === 'num' ? '№' : k === 'sla' ? 'SLA' : k === 'status' ? 'Статус' : 'Обновлён'}
            </button>
          ))}
        </div>
      )}

      <div className="inbx-page__body" style={{ padding: 0 }}>
        <DataTable
          rows={rows}
          columns={columns}
          loading={ticketsQ.isLoading}
          selectable
          selectedIds={selectedIds}
          onSelect={setSelectedIds}
          onRowClick={(t) => void navigate({ to: '/inbox', search: { ticket: t.id } })}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={toggleSort}
        />
      </div>

      {/* C2: New saved view drawer */}
      <NewSavedViewDrawer
        open={newViewOpen}
        currentFilters={filterParams}
        onClose={() => setNewViewOpen(false)}
        onCreate={(name, icon) => {
          createViewMut.mutate({
            name,
            icon,
            filters: { ...filterParams },
            sort: sortKey ? [{ key: sortKey, dir: sortDir }] : [],
          });
          setNewViewOpen(false);
        }}
      />

      {/* C4: Bulk assign drawer */}
      <BulkAssignDrawer
        open={bulkAssignOpen}
        operators={operatorsQ.data ?? []}
        onClose={() => setBulkAssignOpen(false)}
        onAssign={(userId) => {
          [...selectedIds].forEach((id) => assignMut.mutate({ id, userId }));
          setSelectedIds(new Set());
          setBulkAssignOpen(false);
        }}
      />

      {/* C5: Close confirm */}
      <ConfirmModal
        open={!!closeConfirmId}
        title="Закрыть тикет?"
        description="Тикет будет помечен как решённый."
        confirmLabel="Закрыть"
        onConfirm={() => {
          if (closeConfirmId)
            updateMut.mutate({ id: closeConfirmId, patch: { status: 'resolved' } });
          setCloseConfirmId(null);
        }}
        onCancel={() => setCloseConfirmId(null)}
      />
    </div>
  );
}

// ── C2: New Saved View Drawer ────────────────────────────────────────

function NewSavedViewDrawer({
  open,
  currentFilters,
  onClose,
  onCreate,
}: {
  open: boolean;
  currentFilters: Record<string, string>;
  onClose: () => void;
  onCreate: (name: string, icon: string) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('ti-star');

  if (!open) return <></>;

  return (
    <DrawerPanel open={open} title="Новый вид" onClose={onClose} width={360}>
      <Field label="Название">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Например: «VIP клиенты»"
          autoFocus
        />
      </Field>
      <Field label="Иконка">
        <Select value={icon} onChange={(e) => setIcon(e.target.value)}>
          {['ti-star', 'ti-filter', 'ti-inbox', 'ti-user', 'ti-bolt', 'ti-flag'].map((i) => (
            <option key={i} value={i}>
              {i.replace('ti-', '')}
            </option>
          ))}
        </Select>
      </Field>
      <div
        style={{
          padding: '8px 0',
          fontSize: 'var(--inbx-fz-sm)',
          color: 'var(--inbx-text-secondary)',
        }}
      >
        Сохранятся текущие фильтры:{' '}
        {Object.entries(currentFilters).length === 0
          ? 'нет фильтров'
          : Object.entries(currentFilters)
              .map(([k, v]) => `${k}=${v}`)
              .join(', ')}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <Button variant="ghost" onClick={onClose}>
          Отмена
        </Button>
        <Button variant="primary" onClick={() => onCreate(name, icon)} disabled={!name.trim()}>
          Сохранить вид
        </Button>
      </div>
    </DrawerPanel>
  );
}

// ── C4: Bulk Assign Drawer ───────────────────────────────────────────

function BulkAssignDrawer({
  open,
  operators,
  onClose,
  onAssign,
}: {
  open: boolean;
  operators: OperatorRow[];
  onClose: () => void;
  onAssign: (userId: string) => void;
}): JSX.Element {
  const [search, setSearch] = useState('');
  const filtered = operators.filter(
    (o) =>
      o.name.toLowerCase().includes(search.toLowerCase()) ||
      o.email.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <DrawerPanel open={open} title="Назначить оператору" onClose={onClose} width={320}>
      <Field label="Поиск">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Имя или email..."
          autoFocus
        />
      </Field>
      <div style={{ marginTop: 8 }}>
        {filtered.map((op) => (
          <button
            key={op.id}
            type="button"
            onClick={() => onAssign(op.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: '8px 12px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderRadius: 6,
              textAlign: 'left',
            }}
            onMouseEnter={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.background = 'var(--inbx-bg-hover)')
            }
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'none')}
          >
            <OperatorAvatar
              user={op}
              size="sm"
              status={op.status as 'online' | 'busy' | 'away' | 'offline'}
            />
            <div>
              <div style={{ fontWeight: 500 }}>{op.name}</div>
              <div style={{ fontSize: 'var(--inbx-fz-sm)', color: 'var(--inbx-text-tertiary)' }}>
                {op.email}
              </div>
            </div>
          </button>
        ))}
      </div>
    </DrawerPanel>
  );
}
